import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  entitlementsQuery,
  permissionQuery,
  tenantSettingsQuery,
  useScope,
  type PermissionQuery,
} from "./authorization";
import { assuranceQuery, factorsQuery } from "./mfa";
import { useSupabase } from "./session";
import { membershipQuery } from "./tenant";

/**
 * Starts every check a tenant screen waits on as soon as the church and the person are known (#286): the membership
 * and its roles, the church's settings and modules, the sign-in's assurance level and factors, and the permission
 * checks the app names (its navigation, its own gate). Each guard asks for its answer only once the guard above it has
 * answered, so on their own these ran one after another, seven round trips before the first screen on staging; started
 * together they take one. Nothing here decides anything: the guards read the same cached answers, and the server
 * still enforces every read and RPC.
 *
 * Call it once, at the top of an app's tenant tree; `permissions` must be a stable array (a module constant).
 */
export function useStartupChecks(permissions: readonly PermissionQuery[] = []): void {
  const supabase = useSupabase();
  const queryClient = useQueryClient();
  const { tenantId, userId } = useScope();
  useEffect(() => {
    if (tenantId === null || userId === null) return;
    // A failure is left to the guard that needs the answer: it asks again on mount and shows its own error state.
    const ignore = () => undefined;
    queryClient.query(membershipQuery(supabase, tenantId, userId)).catch(ignore);
    queryClient.query(tenantSettingsQuery(supabase, tenantId)).catch(ignore);
    queryClient.query(entitlementsQuery(supabase, tenantId)).catch(ignore);
    queryClient.query(assuranceQuery(supabase, userId)).catch(ignore);
    queryClient.query(factorsQuery(supabase, userId)).catch(ignore);
    for (const q of permissions) {
      queryClient.query(permissionQuery(supabase, tenantId, userId, q)).catch(ignore);
    }
  }, [queryClient, supabase, tenantId, userId, permissions]);
}
